/* eslint-disable react/jsx-one-expression-per-line */
import React, { useState, useEffect } from 'react';
import OrderSummary from 'shared/order-summary';
import { useMutation, useQuery } from '@apollo/react-hooks';
import useGlobal from 'hooks/useGlobal';
import DiscountForm from 'shared/discount-form';
import useLogin from 'hooks/useLogin';
import useForm from 'hooks/useForm';
import Button from 'button/loading';
import Input from 'shared/form-input';
import Hero from 'components/templates-default/cohort-hero';
import setEmailMutation from '@/queries/checkoutEmailUpdate';
import getCheckoutId from '@/queries/getCheckoutId';
import { getCheckoutEmail, getCheckoutWithShipping } from '@/queries/getCart';
import {
  getDefaultAddressQuery,
  getCustomerClient,
  getCustomerCheckout
} from '@/queries/getCustomer';

import { shippingAddressOnly } from '@/queries/checkoutShippingAddressUpdate';
import ShippingForm from './shipping-form';
import Header from '../header';
import CustomerInfo from '../customer-info-block';
import ErrorBlock from '../error-block';

import s from './shipping.module.scss';

function GuestEmailForm({ form }) {
  const { data: checkoutIdData } = useQuery(getCheckoutId);
  const { data } = useQuery(getCheckoutEmail, {
    variables: {
      id: checkoutIdData.checkout.checkoutId
    }
  });

  const { state, events, emit } = form;
  const handleSubmit = e => {
    e.preventDefault();
  };

  useEffect(() => {
    if (data?.node?.email && !state.inputs.email && !state.touched.email) {
      emit.update({
        inputs: {
          email: data.node.email
        },
        touched: {
          email: true
        }
      });
    }
  }, [data, state]);
  return (
    <form onSubmit={handleSubmit} className={s.short}>
      <Input
        placeholder="Email"
        required
        name="email"
        className={s.input}
        state={state}
        {...events}
      />
    </form>
  );
}

function UserEmailForm({ form, submitMethod }) {
  const { state, events } = form;
  const [loading, setLoading] = useState(false);
  const handleSubmit = async e => {
    try {
      setLoading(true);
      e.preventDefault();
      await submitMethod();
    } catch (error) {
      setLoading(false);
      console.log(error);
    }
  };
  return (
    <form onSubmit={handleSubmit}>
      <div className={s.flex}>
        <Input
          placeholder="Email"
          name="email"
          required
          className={s.input}
          state={state}
          {...events}
        />
        <Input
          placeholder="Password"
          name="password"
          required
          type="password"
          className={s.input}
          state={state}
          {...events}
        />
      </div>
      <div className={s.button}>
        <Button
          small
          type="submit"
          disabled={!state.submitReady || loading}
          loading={loading}
        >
          Log in
        </Button>
      </div>
    </form>
  );
}

function EmailSection({ userEmailSection, guestEmailSection, customerInfo }) {
  const { state } = useGlobal();
  const [userForm, setUserForm] = useState(false);
  useEffect(() => {
    if (state.checkout.edit === 'contact') {
      setUserForm(false);
    }
  }, [state.checkout.edit]);

  if (customerInfo.email && state.checkout.edit !== 'contact') {
    return <CustomerInfo customerInfo={customerInfo} />;
  }
  return (
    <div className={s.email}>
      <p className={s.emailTitle}>Contact information</p>
      {userForm ? (
        <UserEmailForm {...userEmailSection} />
      ) : (
        <GuestEmailForm {...guestEmailSection} />
      )}
      <div className={s.formMessage}>
        {userForm ? (
          <p>
            Checkout as{' '}
            <button type="button" onClick={() => setUserForm(!userForm)}>
              guest
            </button>
          </p>
        ) : (
          <p>
            Already have an account?{' '}
            <button type="button" onClick={() => setUserForm(!userForm)}>
              Log in
            </button>
          </p>
        )}
      </div>
    </div>
  );
}

export default function Shipping({ customerInfo }) {
  const [localErrors, setLocalErrors] = useState(false);
  const login = useLogin();
  const {
    data: {
      customer: { customerAccessToken }
    }
  } = useQuery(getCustomerClient);
  const guestEmailForm = useForm({}, ['email']);
  const [setEmail, { loading }] = useMutation(setEmailMutation);
  const [setCheckoutShippingAddress, { loading: settingCSA }] = useMutation(
    shippingAddressOnly
  );
  const { data: checkoutIdData } = useQuery(getCheckoutId);
  const cartShippingResponse = useQuery(getCheckoutWithShipping, {
    variables: {
      id: checkoutIdData.checkout.checkoutId
    }
  });
  const hasShippingAddress = cartShippingResponse?.data?.node || false;

  const {
    data: dataDefaultAddress,
    loading: loadingDefaulAddress,
    error
  } = useQuery(getDefaultAddressQuery, {
    variables: { customerAccessToken }
  });

  // console.log(dataDefaultAddress, 'dataDefaultAddress');

  // change shipping address to be the same as default address
  useEffect(() => {
    if (
      !hasShippingAddress &&
      dataDefaultAddress?.customer?.defaultAddress &&
      !error
    ) {
      const mailingAddressInput = dataDefaultAddress?.customer?.defaultAddress;
      delete mailingAddressInput.id;
      delete mailingAddressInput.__typename;
      setCheckoutShippingAddress({
        variables: {
          shippingAddress: mailingAddressInput || {},
          checkoutId: checkoutIdData.checkout.checkoutId
        },
        refetchQueries: [
          {
            query: getCheckoutWithShipping,
            variables: {
              id: checkoutIdData.checkout.checkoutId
            }
          },
          {
            query: getCheckoutWithShipping,
            variables: {
              id: checkoutIdData.checkout.checkoutId
            }
          },
          {
            query: getDefaultAddressQuery,
            variables: { customerAccessToken }
          }
        ],
        onCompleted: data => console.log(data, 'completed')
      })
        .then(res => {
          console.log(res, 'res');
        })
        .catch(error => console.log(error, '2'));
    }
  }, [dataDefaultAddress?.customer]);

  const submitGuestForm = async checkoutId => {
    try {
      const res = await setEmail({
        variables: {
          checkoutId,
          email: guestEmailForm.state.inputs.email
        }
      });
      console.log(res, 'EMAIL');
      return res;
    } catch (error) {
      console.log(error);
      setLocalErrors(['Error submitting your email. Please try again']);
      throw error;
    }
  };
  const submitUserForm = async () => {
    try {
      const res = await login.handleLogin();
      setLocalErrors(false);
      return res;
    } catch (error) {
      setLocalErrors(false);
      throw error;
    }
  };
  const isUser = login.customerAccessToken;
  const submitEmailReady = guestEmailForm.state.submitReady;
  const loginErrors = login?.errors?.length && login.errors;
  return (
    <main className={s.root}>
      <Hero title="Checkout" />
      <Header />
      <div className="container">
        <div className={s.wrapper}>
          <div className={s.child}>
            {/* <ErrorBlock
              marginBottom={30}
              errors={localErrors || loginErrors || []}
            />
            <EmailSection
              customerInfo={customerInfo}
              guestEmailSection={{
                form: guestEmailForm
              }}
              userEmailSection={{
                form: login.form,
                submitMethod: submitUserForm
              }}
            /> */}
            <ShippingForm
              {...{
                isUser,
                submitEmailReady,
                setLocalErrors,
                submitGuestForm,
                emailLoading: loading,
                emitGuestForm: guestEmailForm.emit,
                cartData: login.cartData
              }}
            />
          </div>
          <div className={s.summary}>
            <DiscountForm />
            <OrderSummary />
          </div>
        </div>
      </div>
    </main>
  );
}
